import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "../../lib/queryClient";
import {
  socialBroadcastService,
  SocialBroadcast,
  SocialBroadcastPayload,
  SocialPlatformConfig,
  SocialPlatformConfigPayload,
  SocialPostLog,
} from "../../services/socialBroadcastService";

/**
 * Hook to query all social media broadcasts
 */
export const useSocialBroadcastsQuery = () => {
  return useQuery<SocialBroadcast[]>({
    queryKey: queryKeys.socialBroadcast.broadcasts(),
    queryFn: ({ signal }) => socialBroadcastService.getBroadcasts(signal),
    refetchInterval: (query) => {
      // Auto-poll every 4s if any broadcast has pending logs
      const data = query.state.data;
      const hasPending = data?.some((b) =>
        b.logs?.some((l) => l.status === "pending")
      );
      return hasPending ? 4000 : false;
    },
  });
};

/**
 * Hook to query a single broadcast detail
 */
export const useSocialBroadcastDetailQuery = (id: number | string | null | undefined) => {
  return useQuery<SocialBroadcast>({
    queryKey: queryKeys.socialBroadcast.broadcastDetail(id || 0),
    queryFn: ({ signal }) => socialBroadcastService.getBroadcastDetail(id!, signal),
    enabled: Boolean(id),
  });
};

/**
 * Mutation to compose and broadcast a new social post
 */
export const useSendBroadcastMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: SocialBroadcastPayload) =>
      socialBroadcastService.sendBroadcast(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.socialBroadcast.all,
      });
    },
  });
};

/**
 * Mutation to delete a broadcast
 */
export const useDeleteBroadcastMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => socialBroadcastService.deleteBroadcast(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.socialBroadcast.all,
      });
    },
  });
};

/**
 * Hook to query social platform configurations
 */
export const useSocialConfigsQuery = () => {
  return useQuery<SocialPlatformConfig[]>({
    queryKey: queryKeys.socialBroadcast.configs(),
    queryFn: ({ signal }) => socialBroadcastService.getConfigs(signal),
  });
};

/**
 * Mutation to create a platform configuration
 */
export const useCreateSocialConfigMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: SocialPlatformConfigPayload) =>
      socialBroadcastService.createConfig(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.socialBroadcast.configs(),
      });
    },
  });
};

/**
 * Mutation to update an existing platform configuration
 */
export const useUpdateSocialConfigMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: number;
      payload: Partial<SocialPlatformConfigPayload>;
    }) => socialBroadcastService.updateConfig(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.socialBroadcast.configs(),
      });
    },
  });
};

/**
 * Mutation to delete a platform configuration
 */
export const useDeleteSocialConfigMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => socialBroadcastService.deleteConfig(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.socialBroadcast.configs(),
      });
    },
  });
};

/**
 * Hook to query execution logs with optional filtering
 */
export const useSocialLogsQuery = (params?: {
  platform?: string;
  status?: string;
  broadcast_id?: number | string;
}) => {
  return useQuery<SocialPostLog[]>({
    queryKey: queryKeys.socialBroadcast.logs(params),
    queryFn: ({ signal }) => socialBroadcastService.getLogs(params, signal),
  });
};
